import { format } from "date-fns";

/** Sentinel picker value meaning "aggregate across every month" (all-time / lifetime view). */
export const ALL_TIME_MONTH_VALUE = "ALL_TIME";

/** Canonical month value used by dashboard month pickers (must match MenuItem values). */
export function formatDashboardMonthValue(date: Date): string {
  return format(date, "yyyy-MM-dd'T'HH:mm:ss'Z'");
}

export function getCurrentDashboardMonthValue(): string {
  const now = new Date();
  return formatDashboardMonthValue(
    new Date(now.getFullYear(), now.getMonth(), 1),
  );
}

/** Build the last N calendar months (newest first) for month selectors. */
export function buildDashboardMonthOptions(count = 12): Array<{
  value: string;
  label: string;
}> {
  const months = [];
  const currentDate = new Date();
  for (let i = 0; i < count; i++) {
    const date = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() - i,
      1,
    );
    months.push({
      value: formatDashboardMonthValue(date),
      label: format(date, "MMMM yyyy"),
    });
  }
  return months;
}

/** Resolve a stored month string to a valid picker value, or current month. */
export function resolveDashboardMonthValue(
  stored: string | null | undefined,
  options: Array<{ value: string }>,
): string {
  const current = getCurrentDashboardMonthValue();
  if (!stored) {
    return options[0]?.value ?? current;
  }
  if (options.some((m) => m.value === stored)) {
    return stored;
  }
  // Legacy values from toISOString() may not match picker format — match by YYYY-MM.
  const ym = stored.slice(0, 7);
  const byMonth = options.find((m) => m.value.startsWith(ym));
  return byMonth?.value ?? options[0]?.value ?? current;
}

/** Convert dashboard month picker value to ops API UTC range (exclusive end). */
export function monthValueToOpsDateRange(monthValue: string): {
  start_date: string;
  end_date: string;
} {
  const { year, month } = monthValueToYearMonth(monthValue);
  return {
    start_date: new Date(Date.UTC(year, month - 1, 1)).toISOString(),
    end_date: new Date(Date.UTC(year, month, 1)).toISOString(),
  };
}

/** Calendar year + month (1–12) for ESG by-branch query params. */
export function monthValueToYearMonth(monthValue: string): {
  year: number;
  month: number;
} {
  const match = monthValue.match(/^(\d{4})-(\d{2})/);
  if (!match) {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  }
  return { year: Number(match[1]), month: Number(match[2]) };
}
